import { ECreditApplicationStatus, EFinancingCompany, EFinancingProgram, ELoanPurpose } from '@if/core/types'
import { getNumberFromQueryString } from '@if/core/utils'
import InsightsIcon from '@mui/icons-material/Insights'
import { Button, Paper, Tab, Tabs, Toolbar } from '@mui/material'
import Box from '@mui/material/Box'
import {
  DataGrid,
  FooterPropsOverrides,
  GridColDef,
  GridPaginationModel,
  GridSlotProps,
  useGridApiRef,
} from '@mui/x-data-grid'
import { enUS, frFR } from '@mui/x-data-grid/locales'
import { AddApplicationButton, AddApplicationIconButton } from '@src/components/AddApplication'
import { useUser, useUserPermissions } from '@src/contexts/AppContext'
import { useUnreadCreditApplicationIds } from '@src/data/api/chat-message/chat-message-api'
import { useCreditApplicationList } from '@src/data/api/credit-api/credit-api'
import { useMerchantById } from '@src/data/api/merchants-api/merchants-api'
import { CreditDashboardFilters } from '@src/data/api/type'
import { merchantSupportsInviteMessages } from '@src/data/merchant-config-selector'
import useFeatureFlags from '@src/hooks/config-hooks'
import { HiddenMerchantCategories } from '@src/types/Constants'
import { JSXElementConstructor, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useCreditDashboardEntriesColumns, useCreditDashboardEntriesQuery } from './browseCreditApplication-hooks'
import { getEndDate, getStartDate } from './browseCreditApplicationPage-selector'
import CreditEntriesFiltersDialog from './components/CreditEntriesFiltersDialog'
import InvitationDialog from './components/InvitationDialog'
import TableFilters from './components/TableFilters'
import { ECreditApplicationStatusFilters } from './components/creditEntriesFiltersDialog-hooks'
import Footer from './components/footer'

const BrowseCreditApplicationPage = () => {
  const {
    i18n: { resolvedLanguage },
    t,
  } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const updateSearch = new URLSearchParams(location.search)
  const user = useUser()
  const { hasDetailAccess, canCreateApplications, isAdmin, canSendWelcomeMessage } = useUserPermissions()
  const { flags } = useFeatureFlags()
  const apiRef = useGridApiRef()
  const query = useCreditDashboardEntriesQuery(searchParams)

  const [creditApplications, isLoadingCreditApps] = useCreditApplicationList(query)
  const [merchant] = useMerchantById(user?.merchantId ?? '')

  const unreadCreditApplicationIds = useUnreadCreditApplicationIds()
  const merchantServiceCategory = merchant?.serviceCategory
  const isVeterinary = merchantServiceCategory === ELoanPurpose.Veterinary
  const isHidden = merchantServiceCategory ? HiddenMerchantCategories.includes(merchantServiceCategory) : false
  const onlyProductsApplications = creditApplications.every(
    (app) => app.financingProgramId === EFinancingProgram.Products,
  )
  const merchantSupportsInviteMessagesFlag = merchant
    ? merchantSupportsInviteMessages(merchant.supportedFinancingPrograms)
    : false
  const supportsMultiplePrograms = merchant ? merchant.supportedFinancingPrograms.length > 1 : false
  // update search
  const [openFilter, setOpenFilter] = useState(false)
  const [isInvitationDialogOpen, setIsInvitationDialogOpen] = useState(false)

  const updateUriQuery = () => {
    const newUrl = `${location.pathname}?${updateSearch.toString()}`
    navigate(newUrl, { replace: true })
  }

  const handleSearch = (value: string) => {
    updateSearch.set('searchValue', encodeURIComponent(value))
    updateUriQuery()
  }

  const handleTabChange = (status: string) => {
    updateSearch.set('creditApplicationStatus', encodeURIComponent(status))
    updateUriQuery()
  }

  const handleFilters = (filters: CreditDashboardFilters) => {
    updateSearch.set('merchantId', encodeURIComponent(filters.merchantId ?? ""))
    updateSearch.set('creditApplicationStatus', encodeURIComponent(filters.creditApplicationStatus ?? ""))
    updateSearch.set('currentCustomerStep', encodeURIComponent(filters.currentCustomerStep ?? ""))
    updateSearch.set('requiresMerchantAttention', encodeURIComponent(filters.requiresMerchantAttention ?? ""))
    updateSearch.set('startDate', encodeURIComponent(filters.startDate ?? ""))
    updateSearch.set('endDate', encodeURIComponent(filters.endDate ?? ""))
    updateSearch.set('originSystemId', encodeURIComponent(filters.originSystemId ?? ""))
    updateSearch.set('createdByUserId', encodeURIComponent(filters.createdByUserId ?? ""))

    updateUriQuery()
    setOpenFilter(false)
  }

  const setPagination = (pagination: GridPaginationModel) => {
    updateSearch.set('offset', encodeURIComponent(query.limit * pagination.page))
    updateSearch.set('limit', encodeURIComponent(pagination.pageSize))
    updateUriQuery()
  }

  const handleOpenInvitationDialog = () => {
    setIsInvitationDialogOpen(true)
  }

  const handleCloseInvitationDialog = () => {
    setIsInvitationDialogOpen(false)
  }

  const pagination = {
    pageSize: getNumberFromQueryString(searchParams, 'limit', 25),
    page: getNumberFromQueryString(searchParams, 'offset', 0) / getNumberFromQueryString(searchParams, 'limit', 25),
  }

  // Table def
  const columns: GridColDef[] = useCreditDashboardEntriesColumns(
    isVeterinary,
    merchant?.canSkipFlinks ?? false,
    hasDetailAccess,
    unreadCreditApplicationIds,
  )

  const localeTextDatagrid =
    resolvedLanguage === 'fr'
      ? frFR.components.MuiDataGrid.defaultProps.localeText
      : enUS.components.MuiDataGrid.defaultProps.localeText

  const handleClearFilter = () => {
    handleFilters({
      merchantId: '',
      creditApplicationStatus: ECreditApplicationStatus.Active,
      currentCustomerStep: '',
      startDate: getStartDate(),
      endDate: getEndDate(),
      originSystemId: '',
      createdByUserId: '',
    } as CreditDashboardFilters)
  }
  const hasAFilter = (): boolean => {
    return (
      ![ECreditApplicationStatusFilters.Active, ECreditApplicationStatusFilters.Draft].includes(
        query.creditApplicationStatus,
      ) ||
      query.merchantId !== '' ||
      query.currentCustomerStep !== '' ||
      query.requiresMerchantAttention ||
      query.startDate !== getStartDate() ||
      query.endDate !== getEndDate() ||
      query.originSystemId !== '' ||
      query.createdByUserId !== ''
    )
  }

  return (
    <>
      <Paper sx={{ display: 'flex', flexDirection: 'column' }}>
        <Toolbar sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <TableFilters
            handleSearch={handleSearch}
            onOpenFilterClicked={() => setOpenFilter(true)}
            creditApplicationStatusFilter={query.creditApplicationStatus}
            handleClearFilter={handleClearFilter}
            hasAFilter={hasAFilter()}
          />
          {merchant && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              {!user?.isBannerUser &&
                canSendWelcomeMessage &&
                merchantSupportsInviteMessagesFlag && (
                  <Button
                    variant="contained"
                    onClick={handleOpenInvitationDialog}
                    sx={{ display: { xs: 'none', md: 'block' } }}
                    color="primary"
                  >
                    {t('viewCreditApplication.invitation')}
                  </Button>
                )}
              {canCreateApplications && (
                <>
                  <AddApplicationButton
                sx={{ display: { xs: 'none', md: 'inline-flex' } }}
                supportedFinancingPrograms={merchant?.supportedFinancingPrograms ?? []}
                variant="contained"
                color="primary"
              />
              <AddApplicationIconButton
                sx={{ display: { md: 'none' } }}
                supportedFinancingPrograms={merchant?.supportedFinancingPrograms ?? []}
                color="primary"
              />{' '}
              {isAdmin && (
                <Button
                  title="Manage users"
                  variant="contained"
                  color="primary"
                  onClick={() => navigate(`/manage-users`)}
                >
                  {t('common.manageUsers')}
                </Button>
              )}
              {user?.rights.canSetReportProperties && flags.UseMerchantReportSettingsMenu && (
                <Button
                  title={t('menus.reportSettings')}
                  variant="contained"
                  color="primary"
                  onClick={() => navigate(`/report-settings`)}
                >
                  {t('menus.reportSettings')}
                </Button>
              )}
              {flags.UseMerchantStatistics && merchant.financingCompanyId === EFinancingCompany.IFinance && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate(`/merchant-statistics`)}
                  sx={{ display: { xs: 'none', md: 'inline-flex' }, gap: 1, alignItems: 'center' }}
                  startIcon={<InsightsIcon />}
                >
                  {t('common.insights')}
                </Button>
              )}
                </>
              )}
            </Box>
          )}
        </Toolbar>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={
              query.creditApplicationStatus === ECreditApplicationStatusFilters.Active ||
              query.creditApplicationStatus === ECreditApplicationStatusFilters.Draft
                ? query.creditApplicationStatus
                : false
            }
            onChange={(_, newStatus: string) => handleTabChange(newStatus)}
          >
            <Tab label={t('common.applications')} value={ECreditApplicationStatus.Active} />
            <Tab label={t('common.drafts')} value={ECreditApplicationStatus.Draft} />
          </Tabs>
        </Box>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <DataGrid
            sx={{
              '.MuiDataGrid-cell': {
                fontWeight: 'bold',
              },
            }}
            rows={creditApplications}
            columns={columns}
            apiRef={apiRef}
            columnVisibilityModel={{
              interestRate: !isHidden,
              merchantName: user?.isBannerUser ?? false,
              maxAmountFinanced: !onlyProductsApplications,
              financingProgramId: supportsMultiplePrograms,
            }}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: query.limit,
                },
              },
            }}
            disableVirtualization
            onPaginationModelChange={(model: GridPaginationModel) => setPagination(model)}
            pageSizeOptions={[25, 50, 100]}
            paginationMode="server"
            rowCount={creditApplications.length}
            loading={isLoadingCreditApps}
            disableRowSelectionOnClick
            disableColumnFilter
            disableColumnMenu
            localeText={localeTextDatagrid}
            slots={{
              footer: Footer as JSXElementConstructor<GridSlotProps['footer']>,
            }}
            slotProps={{
              footer: {
                disabledPreview: query.offset === 0,
                disabledNext: creditApplications.length < query.limit,
                pagination,
                setPagination,
              } as FooterPropsOverrides,
            }}
          />
        </div>
      </Paper>
      <CreditEntriesFiltersDialog
        open={openFilter}
        merchant={merchant}
        filterValues={query}
        onApplyFilters={handleFilters}
        onCancel={() => setOpenFilter(false)}
        handleClearFilter={handleClearFilter}
      />
      <InvitationDialog
        open={isInvitationDialogOpen}
        onClose={handleCloseInvitationDialog}
        financingCompanyId={merchant?.financingCompanyId}
        loanPurposeId={merchant?.serviceCategory ?? null}
      />
    </>
  )
}

export default BrowseCreditApplicationPage
